import type { ApiClient } from '../client/api-client';
import type {
  AmlBillingSettings,
  AmlBillingSettingsPatch,
  AmlChargesResponse,
  AmlChargesSummary,
  AmlRiskPolicyCommandResult,
  AmlRiskPolicyPatch,
  AmlRiskPolicyResponse,
  MerchantAmlBillingPayload,
  MerchantAmlBillingResponse,
  MerchantAmlBillingOverride,
} from '../contracts/aml';
import type { SecondFactor } from '../contracts/security';

const monthQuery = (month: string, extra?: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  search.set('month', month);
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  return search.toString();
};

export function createAmlService(client: ApiClient) {
  return {
    getRiskPolicy: () => client.get<AmlRiskPolicyResponse>('/app/v1/admin/aml-risk-policy'),
    patchRiskPolicy: (payload: AmlRiskPolicyPatch) =>
      client.patch<AmlRiskPolicyCommandResult>('/app/v1/admin/aml-risk-policy', payload),
    approveRiskPolicyChange: (id: string, secondFactor: SecondFactor) =>
      client.post<AmlRiskPolicyCommandResult>(`/app/v1/admin/aml-risk-policy/changes/${encodeURIComponent(id)}/approve`, {
        secondFactor,
      }),
    rejectRiskPolicyChange: (id: string) =>
      client.post<AmlRiskPolicyCommandResult>(`/app/v1/admin/aml-risk-policy/changes/${encodeURIComponent(id)}/reject`, {}),
    cancelRiskPolicyChange: (id: string) =>
      client.post<AmlRiskPolicyCommandResult>(`/app/v1/admin/aml-risk-policy/changes/${encodeURIComponent(id)}/cancel`, {}),
    getBillingSettings: () => client.get<AmlBillingSettings>('/app/v1/admin/aml-billing/settings'),
    patchBillingSettings: (payload: AmlBillingSettingsPatch) =>
      client.patch<AmlBillingSettings>('/app/v1/admin/aml-billing/settings', payload),
    getMerchantBilling: (userId: string | number) =>
      client.get<MerchantAmlBillingResponse>(`/app/v1/admin/aml-billing/merchants/${encodeURIComponent(String(userId))}`),
    setMerchantBilling: (userId: string | number, payload: MerchantAmlBillingPayload) =>
      client.put<MerchantAmlBillingOverride>(
        `/app/v1/admin/aml-billing/merchants/${encodeURIComponent(String(userId))}`,
        payload,
      ),
    getChargesSummary: (month: string) =>
      client.get<AmlChargesSummary>(`/app/v1/admin/aml-charges/summary?${monthQuery(month)}`),
    getAdminCharges: (month: string, merchantId?: string) =>
      client.get<AmlChargesResponse>(`/app/v1/admin/aml-charges?${monthQuery(month, { merchantId })}`),
    downloadAdminCharges: (month: string, merchantId?: string) =>
      client.getBlob(`/app/v1/admin/aml-charges?${monthQuery(month, { merchantId, format: 'csv' })}`),
    markChargesInvoiced: (payload: { merchantId: number; month: string; reason: string; secondFactor: SecondFactor }) =>
      client.post<{ updated: number }>('/app/v1/admin/aml-charges/mark-invoiced', payload),
    waiveCharges: (payload: { ids: string[]; reason: string; secondFactor: SecondFactor }) =>
      client.post<{ updated: number }>('/app/v1/admin/aml-charges/waive', payload),
    getMyCharges: (month: string) => client.get<AmlChargesResponse>(`/app/v1/aml-charges?${monthQuery(month)}`),
  };
}

export type AmlService = ReturnType<typeof createAmlService>;
